// Each deploy's service worker takes over open pages (skipWaiting +
// clientsClaim in vite.config.ts), but a page keeps running the version it
// loaded, and browsers only look for a new worker when a page loads. A phone
// that resumes the app from memory, or a tab left open all evening, can stay
// on an old version for days, launch-day fixes included. This reloads into a
// newer version when it won't get in the way: as it takes over, if the player
// hasn't touched the screen since the app came on screen (opening it counts),
// or else the next time the app comes back on screen. Coming back after a
// while away also asks the browser to look for one. The game survives the
// reload because it lives in localStorage.

/** Away this long (a locked phone between hands, say), coming back also looks
 *  for a newer version. A quick switch to another app doesn't. */
const LOOK_AFTER_AWAY_MS = 5 * 60_000;

const TOUCHES = ['pointerdown', 'keydown', 'wheel'] as const;

let waiting = false;
const waitingListeners = new Set<() => void>();

/** Calls `listener` when a newer version arrived while the player was using
 *  the app, so it can be offered now (UpdateNotification); it applies itself
 *  on their next return. Never when it reloads straight away: an offer would
 *  flash on the page that's about to go. Returns a function that stops. */
export function onUpdateWaiting(listener: () => void): () => void {
  waitingListeners.add(listener);
  if (waiting) listener();
  return () => {
    waitingListeners.delete(listener);
  };
}

/** Applies newer versions at moments the player isn't using the app; returns
 *  a function that stops. */
export function applyUpdatesOnReturn(): () => void {
  const sw = navigator.serviceWorker;
  waiting = false;
  if (!sw) return () => {};

  // A first visit's first worker taking over isn't a newer version.
  let hadController = Boolean(sw.controller);
  let touched = false;
  let hiddenAt: number | null = null;

  const onControllerChange = () => {
    if (!hadController) {
      hadController = true;
      return;
    }
    if (!touched) {
      window.location.reload();
      return;
    }
    waiting = true;
    for (const listener of waitingListeners) listener();
  };

  const onTouch = () => {
    touched = true;
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }
    touched = false;
    if (waiting) {
      window.location.reload();
      return;
    }
    const away = hiddenAt === null ? 0 : Date.now() - hiddenAt;
    hiddenAt = null;
    if (away < LOOK_AFTER_AWAY_MS) return;
    // Offline or unregistered: there's nothing to apply.
    sw.getRegistration()
      .then((registration) => registration?.update())
      .catch(() => undefined);
  };

  sw.addEventListener('controllerchange', onControllerChange);
  document.addEventListener('visibilitychange', onVisibilityChange);
  for (const type of TOUCHES) document.addEventListener(type, onTouch, true);
  return () => {
    sw.removeEventListener('controllerchange', onControllerChange);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    for (const type of TOUCHES) {
      document.removeEventListener(type, onTouch, true);
    }
  };
}
